import React from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Loader2, Clock, CheckCircle2, XCircle } from 'lucide-react';
import {
  ServiceGraphOutlineIcon,
  EscalateIcon,
  AlertCircleIcon,
  TeamIcon,
  CalendarIcon,
  WebhookIcon,
} from '@pagerduty/continuum.icons';
import { DUMMY_TASKS } from './control-panel-dummy-data';
import type { CleanupTask, TaskDomain } from './control-panel-dummy-data';

const DOMAIN_ICON: Record<TaskDomain, React.ElementType> = {
  services: ServiceGraphOutlineIcon,
  'escalation-policies': EscalateIcon,
  incidents: AlertCircleIcon,
  teams: TeamIcon,
  schedules: CalendarIcon,
  integrations: WebhookIcon,
};

const DomainIcon: React.FC<{ domain: TaskDomain }> = ({ domain }) => {
  const Icon = DOMAIN_ICON[domain];
  return <Icon size="sm" variant="subtle" aria-label={domain} />;
};

const StatusIcon: React.FC<{ status: CleanupTask['status'] }> = ({ status }) => {
  const props = { size: 14 };

  if (status === 'running') {
    return (
      <Box
        as="span"
        display="inline-flex"
        sx={{
          '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
          animation: 'spin 1s linear infinite',
        }}
        color="blue.500"
      >
        <Loader2 {...props} />
      </Box>
    );
  }
  if (status === 'pending') return <Box as="span" color="gray.400"><Clock {...props} /></Box>;
  if (status === 'done') return <Box as="span" color="green.500"><CheckCircle2 {...props} /></Box>;
  return <Box as="span" color="red.500"><XCircle {...props} /></Box>;
};

export const TasksSection: React.FC = () => {
  return (
    <Box flex={1} overflowY="auto">
      <Heading size="sm" color="gray.700" mb={3}>
        Tasks
      </Heading>

      <VStack align="stretch" spacing={2}>
        {DUMMY_TASKS.map((task) => (
          <Box
            key={task.id}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            p={2}
          >
            <Flex gap={2} align="flex-start">
              <Box pt="1px" flexShrink={0}>
                <StatusIcon status={task.status} />
              </Box>
              <Box flex={1} minW={0}>
                <Flex align="center" gap={2} mb={0.5}>
                  <Text fontSize="xs" fontWeight="medium" color="gray.800" flex={1} noOfLines={1}>
                    {task.name}
                  </Text>
                  <DomainIcon domain={task.domain} />
                </Flex>
              </Box>
            </Flex>
          </Box>
        ))}
      </VStack>
    </Box>
  );
};
